import cn from 'classnames'
import { isEmpty } from 'lodash'
import React, {
  ButtonHTMLAttributes,
  Children,
  cloneElement,
  FocusEventHandler,
  Fragment,
  isValidElement,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'

import styles from './Select.module.scss'

import { useTimeout } from '~hooks/useTimeout'
import DotIcon from '~icons/dot.svg'
import { stopPropagation } from '~lib/helpers'
import Label from '~lib/ui-kit/Label'
import { LabelProps, PaddingOptions } from '~lib/ui-kit/Label/Label'
import MenuLayout, { MenuVariant } from '~lib/ui-kit/MenuLayout'
import MenuList from '~lib/ui-kit/MenuList'
import Option, { OptionProps } from '~lib/ui-kit/Option'
import Popover, { PopoverProps } from '~lib/ui-kit/Popover'
import Text from '~lib/ui-kit/Text'

export type SelectProps<TOption = unknown, TValue = TOption> = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'value' | 'onChange'
> & {
  as?: React.ElementType
  loading?: boolean
  label?: ReactNode
  icon?: ReactElement
  endIcon?: ReactElement
  variant?: LabelProps['variant']
  menuVariant?: MenuVariant
  size?: LabelProps['size']
  borderRadius?: LabelProps['borderRadius']
  highlight?: boolean
  bordered?: boolean
  interactive?: LabelProps['interactive']
  disabled?: boolean
  readOnly?: boolean
  id?: string
  creatable?: boolean
  multiple?: boolean
  searchable?: boolean
  listboxPlacement?: PopoverProps['placement']
  appendTo?: PopoverProps['appendTo']
  value?: TValue
  onChange?: (value: TValue) => void
  onBlur?: FocusEventHandler<HTMLButtonElement>
  onClick?: MouseEventHandler<HTMLButtonElement>
  onCreate?: (inputValue: string) => Promise<unknown>
  onFocus?: FocusEventHandler<HTMLButtonElement>
  renderIcon?: ((value: TValue) => ReactNode) | false
  renderValue?: ((value: TValue) => ReactNode) | false
  className?: string
  inputPlaceholder?: string
  children?: ReactNode
  trigger?: PopoverProps['trigger']
  header?: string
  fitContent?: boolean
  menuMaxWidth?: number
  menuWidth?: number
  popoverMaxWidth?: number
  padding?: PaddingOptions
  // groupKey - for showing/outputting options which will be grouped by this key.  In this case user will see subHeading and then options, belong to this key (needed to add attr to Option with the same name of groupKey)
  groupKey?: string
  groupOrder?: string[]
  matchParentWidth?: boolean
}

const POPOVER_MAX_WIDTH = 180

function getOptionValue(index: number, nodes: ReactNode) {
  const childrenArr = Children.toArray(nodes)
  const opt = childrenArr[index]
  return isValidElement(opt) ? opt.props.value : undefined
}

// Override value type if it's array
function Select<TOption = unknown, TValue = TOption>({
  as = 'button',
  label,
  loading,
  icon = <DotIcon fill="currentColor" />,
  endIcon,
  bordered,
  interactive,
  disabled,
  variant,
  size,
  borderRadius,
  id,
  creatable,
  listboxPlacement = 'bottom-start',
  appendTo = 'parent',
  multiple,
  readOnly,
  searchable,
  value,
  onChange,
  onCreate,
  renderIcon,
  renderValue,
  className,
  children,
  placeholder,
  inputPlaceholder,
  menuVariant,
  header,
  trigger,
  fitContent,
  menuMaxWidth = POPOVER_MAX_WIDTH,
  menuWidth,
  groupKey,
  groupOrder,
  matchParentWidth,
  ...buttonProps
}: SelectProps<TOption, TValue>) {
  const [showListbox, setShowListbox] = useState(false)
  const [activeOption, setActiveOption] = useState(0)
  const [valueState, setValueState] = useState<TValue>(value)
  const [inputValue, setInputValue] = useState('')
  const [listboxElement, setListboxElement] = useState<HTMLUListElement>(null)
  const [inputElement, setInputElement] = useState<HTMLInputElement>(null)
  const [buttonEl, setButtonEl] = useState<HTMLElement>(null)
  const childrenArray = useMemo(() => Children.toArray(children), [children])
  const { setTimeout } = useTimeout()

  const showCreateOption = useMemo(
    () =>
      searchable &&
      creatable &&
      !isEmpty(inputValue) &&
      !childrenArray.some((node) => isValidElement(node) && node.props.children === inputValue),
    [childrenArray, creatable, inputValue, searchable]
  )

  const selectedOption = useMemo((): ReactElement => {
    if (Array.isArray(valueState)) {
      return undefined
    }

    const elem = childrenArray.find((child) => {
      return isValidElement(child) && child.props.value === valueState
    })

    return isValidElement(elem) ? elem : undefined
  }, [childrenArray, valueState])

  useEffect(() => {
    if (typeof value !== 'undefined') {
      setValueState(value)
    }
  }, [value])

  useEffect(() => {
    if (showListbox) {
      setInputValue('')
      setActiveOption(0)
    }
  }, [showListbox])

  useEffect(() => {
    setTimeout(() => {
      if (showListbox && searchable) {
        inputElement?.focus()
      }

      if (showListbox && !searchable) {
        listboxElement?.focus()
      }
    })
  }, [inputElement, listboxElement, searchable, setTimeout, showListbox])

  const displayValue = useMemo(() => {
    if (renderValue === false) {
      return null
    }

    if (renderValue) {
      return renderValue(valueState)
    }

    if (Array.isArray(valueState)) {
      return valueState.length > 0 ? `${label || ''} (${valueState.length})` : label
    }

    return selectedOption?.props.children || label
  }, [label, renderValue, selectedOption?.props.children, valueState])

  function changeValue(val: unknown) {
    if (!onChange && typeof value === 'undefined') {
      setValueState(val as TValue)
    } else {
      onChange?.(val as TValue)
    }
  }

  function countNodes(nodes: ReactNode) {
    const numOfChildren = Children.count(nodes)
    return showCreateOption ? numOfChildren + 1 : numOfChildren
  }

  async function selectOption(index: number, nodes: ReactNode) {
    const total = countNodes(nodes)
    let val: unknown

    if (showCreateOption && index === total - 1) {
      val = (await onCreate?.(inputValue)) ?? inputValue
    } else if (nodes[index].props.onClick) {
      nodes[index].props.onClick()
      setShowListbox(false)
      buttonEl?.focus({ preventScroll: true })
      return
    } else {
      val = getOptionValue(index, nodes)
    }

    if (multiple) {
      const curr = Array.isArray(valueState) ? valueState : []
      val = curr.includes(val) ? curr.filter((item) => item && item !== val) : [...curr, val].filter(Boolean)
    }

    if (typeof val !== 'undefined') {
      changeValue(val)
    }

    if (!multiple) {
      setShowListbox(false)
      buttonEl?.focus({ preventScroll: true })
    }
  }

  function handleChangeVisible(visible: boolean) {
    if (!visible) {
      buttonEl?.focus({ preventScroll: true })
    }
    setShowListbox(visible)
  }

  function handleChangeInput(e: React.ChangeEvent<HTMLInputElement>) {
    setActiveOption(0)
    setInputValue(e.currentTarget.value)
  }

  function handleMouseEnterOption(e: React.MouseEvent<HTMLDivElement>) {
    const { index } = e.currentTarget.dataset

    if (!Number.isNaN(Number(index))) {
      requestAnimationFrame(() => {
        setActiveOption(Number(index))
      })
    }
  }

  const displayOptions = showListbox
    ? Children.toArray(children)
        .filter(
          (child) =>
            isValidElement(child) &&
            (!inputValue ||
              (typeof child.props.children === 'string' &&
                child.props.children.toLowerCase().includes(inputValue.toLowerCase())))
        )
        .map(
          (child, idx, arr) =>
            isValidElement(child) &&
            cloneElement(child as ReactElement<OptionProps & { 'data-index': number }>, {
              active: idx === activeOption,
              selected: Array.isArray(valueState)
                ? valueState.includes(child.props.value)
                : valueState === child.props.value,
              'data-index': idx,
              onClick: stopPropagation,
              onMouseDown: (e) => {
                e.preventDefault()
                const { index } = e.currentTarget.dataset
                selectOption(Number(index), arr)
              },
              onMouseEnter: handleMouseEnterOption,
            })
        )
    : []

  function handleKeyDownListbox(e: React.KeyboardEvent): void {
    if (((e.key === 'Tab' && !e.shiftKey) || e.key === 'ArrowDown') && showListbox) {
      e.preventDefault()
      const total = countNodes(displayOptions)
      const nextIdx = activeOption >= total - 1 ? 0 : activeOption + 1
      listboxElement?.querySelector(`[data-index="${nextIdx}"]`)?.scrollIntoView({ block: 'nearest' })
      setActiveOption(nextIdx)
    }

    if (((e.key === 'Tab' && e.shiftKey) || e.key === 'ArrowUp') && showListbox) {
      e.preventDefault()
      const total = countNodes(displayOptions)
      const nextIdx = activeOption <= 0 ? total - 1 : activeOption - 1
      listboxElement?.querySelector(`[data-index="${nextIdx}"]`)?.scrollIntoView({ block: 'nearest' })
      setActiveOption(nextIdx)
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      selectOption(activeOption, displayOptions)
    }

    if (e.key === 'Escape') {
      e.stopPropagation()
      setShowListbox(false)
      buttonEl?.focus({ preventScroll: true })
    }

    if (e.key === 'Home') {
      e.preventDefault()
      e.stopPropagation()
      const nextIdx = 0
      listboxElement?.querySelector(`[data-index="${nextIdx}"]`)?.scrollIntoView({ block: 'nearest' })
      setActiveOption(nextIdx)
    }

    if (e.key === 'End') {
      e.preventDefault()
      e.stopPropagation()
      const total = Children.count(children)
      const nextIdx = total - 1
      listboxElement?.querySelector(`[data-index="${nextIdx}"]`)?.scrollIntoView({ block: 'nearest' })
      setActiveOption(nextIdx)
    }
  }

  function handleMouseDownCreate(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault()
    const index = Number(e.currentTarget.dataset.index)

    if (!Number.isNaN(index)) {
      selectOption(index, displayOptions)
    }
  }

  useEffect(() => {
    if (!listboxElement) return
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(({ target }) => {
        const scrollbarVisible = target.scrollHeight > target.clientHeight
        if (scrollbarVisible) {
          target.classList.add(styles.menuWithScroll)
        } else {
          target.classList.remove(styles.menuWithScroll)
        }
      })
    })
    resizeObserver.observe(listboxElement)
    // eslint-disable-next-line consistent-return
    return () => resizeObserver.disconnect()
  }, [listboxElement])

  const options =
    !isEmpty(displayOptions) && groupKey
      ? displayOptions.reduce((acc, v) => {
          acc[v.props[groupKey]] = acc[v.props[groupKey]] || []
          acc[v.props[groupKey]].push(v)
          return acc
        }, {})
      : !isEmpty(displayOptions) && !groupKey
      ? displayOptions
      : []

  const groupedOptions = Array.isArray(options)
    ? // Options has no groups, return null
      null
    : groupOrder?.length
    ? // If a group order is provided, return the options in that order
      groupOrder.filter((key) => options[key]?.length).map((key) => [key, options[key]])
    : // Otherwise, return groups as it's returned from `entries`
      Object.entries(options)

  return (
    <>
      <Label
        as={as}
        className={cn(readOnly && styles.readOnly, className, 'px-0')}
        aria-expanded={showListbox}
        bordered={bordered}
        disabled={disabled || readOnly}
        icon={renderIcon === false ? null : renderIcon?.(valueState) || selectedOption?.props.icon || icon} // FIXME
        endIcon={endIcon}
        id={id}
        interactive={interactive}
        ref={setButtonEl}
        variant={variant}
        size={size}
        borderRadius={borderRadius}
        {...buttonProps}
      >
        {displayValue || placeholder}
      </Label>

      <Popover
        trigger={trigger}
        anchorEl={buttonEl}
        disabled={disabled || readOnly}
        open={showListbox}
        onChangeOpen={handleChangeVisible}
        placement={listboxPlacement}
        offset={8}
        maxWidth={menuMaxWidth < menuWidth ? menuWidth : menuMaxWidth}
        width={menuWidth}
        appendTo={appendTo}
        lockBody
        fitContent={fitContent}
        matchParentWidth={matchParentWidth}
      >
        <MenuLayout variant={menuVariant}>
          {searchable && (
            <MenuLayout.Header>
              <input
                className={styles.input}
                ref={setInputElement}
                type="text"
                value={inputValue}
                placeholder={inputPlaceholder}
                onChange={handleChangeInput}
                onKeyDown={handleKeyDownListbox}
                onClick={stopPropagation}
              />
            </MenuLayout.Header>
          )}

          <MenuList ref={setListboxElement} role="listbox" onKeyDown={handleKeyDownListbox}>
            {header && (
              <Text as="div" className={styles.header} type="caption" bold>
                {header}
              </Text>
            )}

            {/* Render Grouped Options if present */}
            {!!groupedOptions &&
              groupedOptions.map(([label, items]) => (
                <Fragment key={label}>
                  <Text className={styles.subHeader} type="caption" variant="grey" bold>
                    {label}
                  </Text>
                  {items as ReactElement}
                </Fragment>
              ))}

            {/* Render basic options otherwise (if type Array) */}
            {Array.isArray(options) ? options : null}

            {searchable && displayOptions.length === 0 && <Option disabled>No result</Option>}

            {showCreateOption && (
              <Option
                active={activeOption === displayOptions.length}
                data-index={displayOptions.length}
                disabled={loading}
                onClick={stopPropagation}
                onMouseDown={handleMouseDownCreate}
                onMouseEnter={handleMouseEnterOption}
              >
                {`Add "${inputValue}"`}
              </Option>
            )}
          </MenuList>
        </MenuLayout>
      </Popover>
    </>
  )
}

export default Select
